import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiEdit2, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";

const STATUS_COLOR = {
  draft:    { bg: "#f1f5f9", text: "#64748b" },
  active:   { bg: "#dcfce7", text: "#15803d" },
  inactive: { bg: "#fef9c3", text: "#a16207" },
  archived: { bg: "#f1f5f9", text: "#94a3b8" },
};

export default function AdminProductPreview() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [mainImg, setMainImg]           = useState("");
  const [imgIdx, setImgIdx]             = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/catalog/products/${id}`);
        const p = data.product;
        // Normalise JSON fields that may arrive as strings or null for brand-new products
        const toArr = (v) => { if (Array.isArray(v)) return v; if (typeof v === "string") { try { const r = JSON.parse(v); return Array.isArray(r) ? r : []; } catch { return []; } } return []; };
        p.variants       = toArr(p.variants).map(v => ({ ...v, pricing: toArr(v.pricing), selectedOptions: toArr(v.selectedOptions) }));
        p.galleryImages  = toArr(p.galleryImages);
        p.tags           = toArr(p.tags);
        p.specifications = toArr(p.specifications);
        p.productOptions = toArr(p.productOptions);
        setProduct(p);

        // Pick first active variant
        const firstActive = p.variants.find(v => v.status === "active");
        setSelectedVariant(firstActive || p.variants[0] || null);

        const imgs = p.galleryImages.length ? p.galleryImages : (p.mainImage ? [p.mainImage] : []);
        setMainImg(imgs[0] || "");
      } catch { toast.error("Failed to load product"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div style={{ padding: 40, color: "#64748b" }}>Loading preview…</div>;
  if (!product) return <div style={{ padding: 40, color: "#ef4444" }}>Product not found.</div>;

  // Defensive helper — DB sometimes returns JSON fields as strings or null for new products
  const safeArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };

  const galleryImages  = safeArray(product.galleryImages);
  const images         = galleryImages.length ? galleryImages : (product.mainImage ? [product.mainImage] : []);
  const allVariants    = safeArray(product.variants).map(v => ({ ...v, pricing: safeArray(v.pricing), selectedOptions: safeArray(v.selectedOptions) }));
  const activeVariants = allVariants.filter(v => v.status === "active");

  /* Resolved starting price */
  const startingPrice = product.startingPrice != null
    ? `Starting from ₹${parseFloat(product.startingPrice).toLocaleString("en-IN")}`
    : "Price on Request";

  /* Pricing tiers for selected variant */
  const pricingTiers = selectedVariant?.pricing
    ? [...selectedVariant.pricing].sort((a, b) => a.quantity - b.quantity)
    : [];

  /* Specs */
  const specs = safeArray(product.specifications);

  /* Option groups for display */
  const productOptions = safeArray(product.productOptions);

  /* Unique option group names from active variants */
  const optionGroupsFromVariants = [];
  const seenGroups = new Set();
  for (const v of activeVariants) {
    for (const o of (v.selectedOptions || [])) {
      if (!seenGroups.has(o.optionGroupId)) {
        seenGroups.add(o.optionGroupId);
        optionGroupsFromVariants.push({ id: o.optionGroupId, name: o.optionGroupName });
      }
    }
  }

  const handleImgNav = (dir) => {
    const next = (imgIdx + dir + images.length) % images.length;
    setImgIdx(next);
    setMainImg(images[next]);
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1000 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <div>
          <button onClick={() => navigate(`/admin/products/${id}/edit`)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontSize: 13, padding: 0, marginBottom: 4 }}>← Back to Edit</button>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Product Preview</h2>
            <span style={{ fontSize: 11, fontWeight: 600, background: STATUS_COLOR[product.status]?.bg, color: STATUS_COLOR[product.status]?.text, padding: "2px 10px", borderRadius: 20 }}>{product.status}</span>
          </div>
        </div>
        <button onClick={() => navigate(`/admin/products/${id}/edit`)} style={editBtn}>
          <FiEdit2 size={13} style={{ marginRight: 6 }} />Edit Product
        </button>
      </div>

      <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
        {/* Left: Images */}
        <div style={{ flex: "0 0 360px", maxWidth: 360 }}>
          {/* Main image */}
          <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 10 }}>
            {mainImg
              ? <img src={mainImg} alt={product.name} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }} />
              : <div style={{ width: "100%", aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>No Image</div>
            }
            {images.length > 1 && (
              <>
                <button onClick={() => handleImgNav(-1)} style={navBtn("left")}><FiChevronLeft size={16} /></button>
                <button onClick={() => handleImgNav(1)}  style={navBtn("right")}><FiChevronRight size={16} /></button>
                <div style={{ position: "absolute", bottom: 8, right: 10, background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 11, borderRadius: 12, padding: "2px 8px" }}>{imgIdx + 1}/{images.length}</div>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {images.map((url, i) => (
                <div key={i} onClick={() => { setMainImg(url); setImgIdx(i); }} style={{ width: 58, height: 58, borderRadius: 7, overflow: "hidden", border: `2px solid ${i === imgIdx ? "#6366f1" : "#e2e8f0"}`, cursor: "pointer" }}>
                  <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Details */}
        <div style={{ flex: 1, minWidth: 280 }}>
          {/* Category breadcrumb */}
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>
            {product.category?.name}{product.subcategory ? ` › ${product.subcategory.name}` : ""}
          </div>

          {/* Name */}
          <h1 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>{product.name}</h1>

          {/* SKU */}
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12, fontFamily: "monospace" }}>SKU: {product.sku}</div>

          {/* Starting price */}
          <div style={{ fontSize: 22, fontWeight: 700, color: product.startingPrice != null ? "#15803d" : "#64748b", marginBottom: 16 }}>
            {startingPrice}
          </div>

          {/* Short description */}
          {product.shortDescription && (
            <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6, margin: "0 0 16px" }}>{product.shortDescription}</p>
          )}

          {/* Variant options selector */}
          {optionGroupsFromVariants.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {optionGroupsFromVariants.map(group => {
                const valuesForGroup = [...new Set(
                  activeVariants.flatMap(v =>
                    (v.selectedOptions || [])
                      .filter(o => o.optionGroupId === group.id)
                      .map(o => o.optionValue)
                  )
                )];

                const selectedValue = selectedVariant?.selectedOptions
                  ?.find(o => o.optionGroupId === group.id)?.optionValue;

                return (
                  <div key={group.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{group.name}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {valuesForGroup.map(val => (
                        <button
                          key={val}
                          onClick={() => {
                            const match = activeVariants.find(v =>
                              v.selectedOptions?.some(o => o.optionGroupId === group.id && o.optionValue === val)
                            );
                            if (match) setSelectedVariant(match);
                          }}
                          style={{
                            padding: "6px 14px", fontSize: 13, border: `1.5px solid ${selectedValue === val ? "#6366f1" : "#e2e8f0"}`,
                            borderRadius: 7, cursor: "pointer", fontWeight: selectedValue === val ? 600 : 400,
                            background: selectedValue === val ? "#eef2ff" : "#fff",
                            color: selectedValue === val ? "#6366f1" : "#374151",
                          }}
                        >{val}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected variant */}
          {selectedVariant && (
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>Selected: </span>{selectedVariant.name}
              <span style={{ color: "#94a3b8", marginLeft: 10, fontFamily: "monospace", fontSize: 12 }}>{selectedVariant.sku}</span>
            </div>
          )}

          {/* Pricing tiers */}
          {pricingTiers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Quantity Pricing</div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ padding: "7px 12px", textAlign: "left", fontWeight: 600, color: "#64748b" }}>Quantity</th>
                      <th style={{ padding: "7px 12px", textAlign: "right", fontWeight: 600, color: "#64748b" }}>Price</th>
                      <th style={{ padding: "7px 12px", textAlign: "right", fontWeight: 600, color: "#64748b" }}>Per Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingTiers.map((tier, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "7px 12px", fontWeight: 500 }}>{tier.quantity.toLocaleString("en-IN")}</td>
                        <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700 }}>₹{(parseFloat(tier.price) * tier.quantity).toLocaleString("en-IN")}</td>
<td style={{ padding: "7px 12px", textAlign: "right", color: "#64748b" }}>
  ₹{parseFloat(tier.price).toFixed(2)}
</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedVariant && (
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 5 }}>
                  Min: {selectedVariant.minimumQuantity} · Increment: {selectedVariant.quantityIncrement}
                </div>
              )}
            </div>
          )}

          {pricingTiers.length === 0 && selectedVariant && (
            <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#854d0e", marginBottom: 16 }}>
              ⚠ No pricing set for this variant.
            </div>
          )}

          {/* Tags */}
          {safeArray(product.tags).length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {safeArray(product.tags).map(tag => (
                <span key={tag} style={{ fontSize: 11, background: "#f1f5f9", color: "#64748b", padding: "3px 10px", borderRadius: 20 }}>{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Specifications */}
      {specs.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Specifications</h3>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden" }}>
            {specs.map((s, i) => (
              <div key={i} style={{ display: "flex", borderBottom: i < specs.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                <div style={{ width: 180, padding: "10px 14px", fontWeight: 600, fontSize: 13, background: "#f8fafc", borderRight: "1px solid #f1f5f9" }}>{s.label}</div>
                <div style={{ padding: "10px 14px", fontSize: 13, flex: 1 }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed description */}
      {product.detailedDescription && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Description</h3>
          <div
            style={{ fontSize: 14, lineHeight: 1.8, color: "#374151" }}
            dangerouslySetInnerHTML={{ __html: product.detailedDescription }}
          />
        </div>
      )}

      {/* Variants overview */}
      {allVariants.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>All Variants</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {allVariants.map(v => (
              <div key={v.id} style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, opacity: v.status === "archived" ? 0.5 : 1 }}>
                <span style={{ fontWeight: 600 }}>{v.name}</span>
                <span style={{ color: "#94a3b8", marginLeft: 6 }}>{v.status}</span>
                {v.pricing.length > 0 && (
                  <span style={{ color: "#15803d", marginLeft: 6 }}>
                    from ₹{Math.min(...v.pricing.map(p => parseFloat(p.price))).toLocaleString("en-IN")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SEO info */}
      <div style={{ marginTop: 28, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 9, padding: "14px 16px" }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>SEO</div>
        <div style={{ fontSize: 13, marginBottom: 4 }}><span style={{ color: "#64748b" }}>Slug: </span>/{product.slug}</div>
        {product.seoTitle && <div style={{ fontSize: 13, marginBottom: 4 }}><span style={{ color: "#64748b" }}>SEO Title: </span>{product.seoTitle}</div>}
        {product.seoDescription && <div style={{ fontSize: 13 }}><span style={{ color: "#64748b" }}>Meta Desc: </span>{product.seoDescription}</div>}
      </div>
    </div>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const editBtn = { display: "inline-flex", alignItems: "center", background: "var(--primary,#6366f1)", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 500 };
const navBtn = (side) => ({
  position: "absolute", top: "50%", transform: "translateY(-50%)",
  [side]: 8, background: "rgba(255,255,255,0.85)", border: "none",
  borderRadius: "50%", width: 32, height: 32, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
});